import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Box, Grid } from '@mui/material';
import {
  DriveEtaOutlined as DriveEtaIcon,
  LocalGasStationOutlined as LocalGasStationIcon,
  NatureOutlined as NatureIcon,
  OpenInNew as OpenInNewIcon,
  //  PhoneIphoneOutlined as PhoneIphoneIcon,
  CloudQueue as CloudQueueIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

import { formatBuildingRates } from '../../lib/formatData';
import useStores from '../../stores/useStores';

import Chart from '../building/Chart';
import DataGrid from '../dataGrid/DataGrid';
import DataGridItem from '../dataGrid/DataGridItem';
import Spinner from '../general/LoadingSpinner';
import QuickMenu from '../quickMenu/QuickMenu';
import FinancialsSummary from './FinancialsSummary';
import DataSection from '../dataSection/DataSection';
import CashFlowChart from './CashFlowChart';
import ScatterPlot from './ScatterPlot';
import './Financials.sass';
import Heatmap from '../chart/Heatmap';

const Financials = ({
  disablePadding = false,
  buildingId,
  projectId,
  isVendor = false,
  isAdmin = false
}) => {
  const history = useHistory();
  const {
    buildingStore,
    projectDetailStore,
    buildingDetailStore,
    userStore
  } = useStores();
  const store = buildingId ? buildingDetailStore : projectDetailStore;
  const [, setFormattedRates] = useState(null);
  const [PPAMode] = useState('customer');
  const [quickMenuAnchor, setQuickMenuAnchor] = useState(null);
  const building = buildingStore.buildings.get(buildingId);
  const isProjectHome = projectId && !buildingId;
  let financialData = isProjectHome
    ? buildingStore.financials?.financials
    : store.formattedFinancials;
  const { financialMode } = buildingDetailStore;

  if (buildingId && financialMode === 'ppa') {
    financialData = buildingDetailStore.formattedFinancialsPPA;
  }

  const formattedFinancialsPPA = buildingId
    ? buildingDetailStore.formattedFinancialsPPA
    : null;

  const ppaData = formattedFinancialsPPA
    ? [
        {
          field: 'ppa_kwh_rate',
          category: 'customer'
        },
        {
          field: 'total_1st_year_net_revenue',
          category: 'customer'
        },
        /*
    {
      label: 'Lifetime Savings',
      field: 'ppa_20_year_savings_to_customer',
      category: 'customer'
    },
    {
      label: 'Rate Discount',
      field: '',
      category: 'customer'
    },
    */
        {
          field: 'total_gross_project_cost',
          category: 'owner'
        },
        {
          field: 'payback_period',
          category: 'owner'
        },
        {
          field: 'IRR',
          category: 'owner'
        },
        {
          field: 'total_1st_year_net_revenue',
          category: 'owner'
        },
        {
          field: 'total_30_year_net_revenue',
          category: 'owner'
        }
      ]
        .map(item => {
          const data = formattedFinancialsPPA.find(e => e.id === item.field);

          if (data) {
            return {
              ...data,
              category: item.category
            };
          }
          return null;
        })
        .filter(e => e)
    : null;

  const energyGenerationData = buildingDetailStore.formattedFinancials.filter(
    item =>
      [
        'solar_potential_gen_y1',
        'fuel_cell_potential_gen_y1',
        'chp_potential_gen_y1',
        'year_1_generation'
      ].includes(item.id)
  );

  const financialsLoading =
    isProjectHome && buildingStore.financials?.financials_loading;

  const emissionsData = isProjectHome
    ? buildingStore?.emissions
    : store.emissionsData;

  let { cashFlow: cashFlowData } = store;

  if (buildingId && financialMode === 'ppa') {
    cashFlowData = buildingDetailStore.cashFlowPPA;
  }

  const {
    location: { hash }
  } = history;

  const activeHash = hash && hash.replace('#', '');
  const { quickMenuLinks } = buildingDetailStore;

  useEffect(() => {
    if (building?.rates) {
      setFormattedRates(formatBuildingRates(building?.rates));
    }
  }, [building]);

  if (!financialData) {
    return (
      <div className="financials">
        <Spinner />
      </div>
    );
  }

  const scatterPlotData = projectDetailStore.buildings
    .filter(building => building.financials)
    .map(building => {
      const { total_gross_project_cost, payback_period } = building.financials;

      return {
        x: total_gross_project_cost,
        y: payback_period,
        id: building.id,
        address: building.address
      };
    });

  /*
  const emissionIcons = {
    smartphones_charged: PhoneIphoneIcon,
    passenger_vehicles_driven_for_one_year: DriveEtaIcon,
    gallons_of_gasoline_consumed: LocalGasStationIcon,
    tree_seedlings_grown_for_10_years: NatureIcon,
    annual_co2_kg_reduced: CloudQueueIcon
  };
  */

  return (
    <div
      className="financials"
      style={{
        padding: disablePadding ? '0' : '16px 32px 48px'
      }}
    >
      {financialData && (
        <DataSection
          id="financials"
          isActive={activeHash === 'financials'}
          setQuickMenuAnchor={setQuickMenuAnchor}
        >
          <FinancialsSummary
            financialMode={financialMode}
            buildingId={buildingId}
            data={financialData}
            loading={financialsLoading}
            isAdmin={isAdmin}
            isVendor={isVendor}
          />
        </DataSection>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          {emissionsData && (
            <DataSection
              id="environmental"
              isActive={activeHash === 'environmental'}
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Annual environmental savings"
            >
              <Grid container spacing={2}>
                <DataGridItem
                  Icon={DriveEtaIcon}
                  label="Vehicles driven"
                  md={12}
                  value={emissionsData.passenger_vehicles_driven_for_one_year}
                  valueProps={{
                    variant: 'h5'
                  }}
                  isLoading={financialsLoading}
                />
                <DataGridItem
                  Icon={LocalGasStationIcon}
                  label="Gallons of gasoline consumed"
                  md={12}
                  value={emissionsData.gallons_of_gasoline_consumed}
                  valueProps={{
                    variant: 'h5'
                  }}
                  isLoading={financialsLoading}
                />
                <DataGridItem
                  Icon={NatureIcon}
                  label="Trees grown for 10 years"
                  md={12}
                  value={emissionsData.tree_seedlings_grown_for_10_years}
                  valueProps={{
                    variant: 'h5'
                  }}
                  isLoading={financialsLoading}
                />
                <DataGridItem
                  Icon={CloudQueueIcon}
                  label={
                    <>
                      kg CO<sub>2</sub> Reduced Annually
                    </>
                  }
                  md={12}
                  value={emissionsData.annual_co2_kg_reduced}
                  valueProps={{
                    variant: 'h5'
                  }}
                  isLoading={financialsLoading}
                />
              </Grid>
            </DataSection>
          )}
        </Grid>
        {cashFlowData?.length && (
          <Grid item xs={12} lg={6}>
            <DataSection
              id="cashflow"
              isActive={activeHash === 'cashflow'}
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Cumulative project cash flow"
            >
              <CashFlowChart
                style={{
                  marginLeft: -16
                }}
                data={cashFlowData}
              />
              {store.analyticsUrl && (
                <Button
                  color="primary"
                  endIcon={<OpenInNewIcon />}
                  href={`https://calculator.gridmarket.com/projects/${store.analyticsUrl}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Detailed calculation
                </Button>
              )}
            </DataSection>
          </Grid>
        )}

        {projectId && !buildingId && (
          <Grid item xs={12} lg={6}>
            <DataSection
              style={{
                height: '100%'
              }}
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Insights"
            >
              <ScatterPlot
                xLabel="Payback Period"
                yLabel="Total Gross Project Cost"
                data={scatterPlotData}
                onBrush={(range, selection, points) => {
                  if (range === null || points.size === 0) {
                    buildingStore.setFilter(
                      'financial_total_gross_project_cost',
                      null
                    );
                    buildingStore.setFilter('financial_payback_period', null);
                    return;
                  }

                  buildingStore.setFilter(
                    'financial_total_gross_project_cost',
                    {
                      start: range.x[0],
                      end: range.x[1]
                    }
                  );

                  buildingStore.setFilter('financial_payback_period', {
                    start: range.y[1],
                    end: range.y[0]
                  });
                }}
              />
            </DataSection>
          </Grid>
        )}

        {false && buildingId && ppaData && (
          <Grid item xs={12}>
            <DataSection
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Financials (PPA)"
            >
              <DataGrid
                data={ppaData
                  .filter(rate => rate.category === PPAMode)
                  .map(rate => ({
                    label: rate.label,
                    value: rate.value,
                    suffix: rate.unit,
                    format: rate.format
                  }))}
              />
            </DataSection>
          </Grid>
        )}
        <Grid item xs={12} sx={{ mt: 6 }}>
          <DataSection
            setQuickMenuAnchor={setQuickMenuAnchor}
            title="Load Profile"
          >
            <Chart
              chartType="load_curve"
              projectId={projectId}
              buildingId={buildingId}
              showMenu={userStore.isAdmin}
            />
          </DataSection>
        </Grid>
        {!isProjectHome && energyGenerationData.length > 0 && (
          <Grid item xs={12} sx={{ mt: 6 }}>
            <DataSection
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Year 1 Energy Generation"
            >
              <DataGrid data={energyGenerationData} />
            </DataSection>
          </Grid>
        )}
        <Grid item xs={12} sx={{ mt: 6 }}>
          <DataSection
            setQuickMenuAnchor={setQuickMenuAnchor}
            title="Energy Consumption"
          >
            <Box align="center">
              <Heatmap
                width={1000}
                height={370}
                buildingId={buildingId}
                projectId={projectId}
              />
            </Box>
          </DataSection>
        </Grid>
      </Grid>

      {building?.rates && (
        <DataSection
          id="rates"
          isActive={activeHash === 'rates'}
          setQuickMenuAnchor={setQuickMenuAnchor}
          title="Rates"
        >
          <DataGrid
            data={formatBuildingRates(building?.rates).map(rate => ({
              label: rate.label,
              value: rate.value,
              suffix: rate.unit,
              format: rate.format
            }))}
          />
        </DataSection>
      )}

      {financialData === null && <Spinner />}
      <QuickMenu
        anchorEl={quickMenuAnchor}
        links={quickMenuLinks}
        onClose={() => setQuickMenuAnchor(null)}
      />
    </div>
  );
};

export default observer(Financials);
